import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useDebounce } from "@/hooks/use-debounce";

type BrandOnboardingStatus = {
  user_id: string;
  brand_id?: string;
  onboarding_status: string;
  completed_steps: string[];
  next_step?: string;
  progress_percentage: number;
} | null;

interface BrandOnboardingContextType {
  status: BrandOnboardingStatus;
  isLoading: boolean;
  refreshStatus: () => Promise<void>;
  lastUpdated: number;
  isCompletingOnboarding: boolean;
  setIsCompletingOnboarding: (value: boolean) => void;
}

const BrandOnboardingContext = createContext<BrandOnboardingContextType | undefined>(undefined);

export function BrandOnboardingProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<BrandOnboardingStatus>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(0);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const { user } = useAuthStore();
  
  // Request deduplication
  const activeRequestRef = useRef<Promise<void> | null>(null);
  const lastRequestTimeRef = useRef(0);
  const MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests
  
  // Debounce user changes to prevent rapid API calls
  const debouncedUserId = useDebounce(user?._id, 200);

  const refreshStatus = useCallback(async () => {
    // Prevent rapid successive calls
    const now = Date.now();
    if (now - lastRequestTimeRef.current < MIN_REQUEST_INTERVAL) {
      console.log("⏸️ BrandOnboardingContext: Request throttled, too soon since last call");
      return;
    }
    
    // Return existing promise if request is already in flight
    if (activeRequestRef.current) {
      console.log("⏸️ BrandOnboardingContext: Request already in progress, returning existing promise");
      return activeRequestRef.current;
    }
    
    // Check user role before making brand-specific API calls
    // Allow the call if user role is brand_user OR if role is not yet determined (could be loading)
    if (user && user.role && user.role !== 'brand_user' && user.role !== 'admin') {
      console.log("User is not a brand user, skipping brand profile fetch", { role: user.role });
      setStatus(null);
      setIsLoading(false);
      return;
    }
    
    lastRequestTimeRef.current = now;
    
    const requestPromise = (async () => {
      try {
        setIsLoading(true);
        
        console.log("🔄 BrandOnboardingContext: refreshStatus called", {
          timestamp: new Date().toISOString(),
          userId: user?._id,
          userRole: user?.role
        });
        
        console.log("🌐 BrandOnboardingContext: Making API call to getBrandCurrentUser");
        const brandProfile = await apiClient.getBrandCurrentUser();
        
        const newStatus = {
          user_id: brandProfile.user._id,
          brand_id: brandProfile.brand?._id || brandProfile.brand?.id,
          onboarding_status: brandProfile.onboarding_status,
          completed_steps: [],
          next_step: brandProfile.next_step,
          progress_percentage: brandProfile.progress_percentage
        };
        
        console.log("✅ BrandOnboardingContext: API call successful", {
          newStatus: newStatus.onboarding_status,
          previousStatus: status?.onboarding_status,
          changed: newStatus.onboarding_status !== status?.onboarding_status
        });
        
        setStatus(newStatus);
        setLastUpdated(Date.now());
        
      } catch (error) {
        console.error("❌ BrandOnboardingContext: Failed to fetch brand onboarding status", error);
        setStatus(null);
      } finally {
        setIsLoading(false);
        activeRequestRef.current = null;
      }
    })();
    
    activeRequestRef.current = requestPromise;
    return requestPromise;
  }, [user, status?.onboarding_status]);

  // Initial load effect - only runs when user ID changes (debounced)
  useEffect(() => {
    console.log("🔍 BrandOnboardingContext: User effect triggered", {
      timestamp: new Date().toISOString(),
      userId: debouncedUserId,
      hasToken: !!sessionStorage.getItem('safari_auth_token'),
      isAuthenticated: useAuthStore.getState().isAuthenticated
    });
    
    const hasToken = sessionStorage.getItem('safari_auth_token') || useAuthStore.getState().isAuthenticated;
    
    if (hasToken && debouncedUserId) {
      console.log("🚀 BrandOnboardingContext: Conditions met, calling refreshStatus");
      refreshStatus();
    } else {
      console.log("🔒 BrandOnboardingContext: No token or user found, setting loading to false");
      setIsLoading(false);
    }
  }, [debouncedUserId, refreshStatus]);

  return (
    <BrandOnboardingContext.Provider value={{ status, isLoading, refreshStatus, lastUpdated, isCompletingOnboarding, setIsCompletingOnboarding }}>
      {children}
    </BrandOnboardingContext.Provider>
  );
}

export function useBrandOnboarding() {
  const context = useContext(BrandOnboardingContext);
  if (context === undefined) {
    throw new Error("useBrandOnboarding must be used within a BrandOnboardingProvider");
  }
  return context;
}